import { AnimatePresence, motion } from 'framer-motion';
import Fuse from 'fuse.js';
import React, {
	type Dispatch,
	type SetStateAction,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useTranslationContext } from '@/context/translation';
import { useDebounce, useTranslationWithLocale } from '@/hooks';
import { AmplitudeService } from '@/services';
import {
	type LandingItem,
	type LandingTool,
	type LandingTools,
} from '@/data/aiToolLandings';
import { AMPLITUDE_EVENTS } from '@/constants';
import {
	nothingFoundVariants,
	searchResultsVariants,
	searchVariants,
} from '@/animations';
import CloseIcon from '@/public/icons/cross.svg';
import styles from './Search.module.scss';

interface SearchDesktopProps {
	data: LandingTools['hero'];
	setActiveTabId: Dispatch<SetStateAction<string>>;
	setSearchActive: Dispatch<SetStateAction<boolean>>;
	setActivePositioningId: Dispatch<SetStateAction<string | null>>;
	setActiveFeature: Dispatch<SetStateAction<LandingTool | null>>;
	setPinnedFeatureId: Dispatch<SetStateAction<string | null>>;
}

export const SearchDesktop = ({
	data,
	setSearchActive,
	setActiveTabId,
	setActivePositioningId,
	setActiveFeature,
	setPinnedFeatureId,
}: SearchDesktopProps) => {
	const { locale, translations } = useTranslationContext();
	const { t } = useTranslationWithLocale(
		'landing-config',
		locale,
		translations?.['landing-config'] as Record<string, unknown> | undefined,
	);
	const { t: tTools } = useTranslationWithLocale(
		'tools',
		locale,
		translations?.['tools'] as Record<string, unknown> | undefined,
	);
	const searchRef = useRef<HTMLDivElement>(null);
	const searchResultsRef = useRef<HTMLDivElement>(null);
	const [searchQuery, setSearchQuery] = useState('');
	const [maxHeight, setMaxHeight] = useState<number | null>(null);

	// Creates a new memoized array of landings for Fuse search, extending every landing item with a 'tags' key.
	// The 'tags' key consists of category tags + group title + item title.
	const landingArray = useMemo(() => {
		return Object.values(data.toolsListing).flatMap(category => {
			const categoryTags = category.tags.map(tag => tag.toLowerCase());
			return category.tools.flatMap(toolGroup => {
				const groupTitle = toolGroup.titleEng.toLowerCase();
				return toolGroup.list.map(item => ({
					...item,
					tags: [...categoryTags, groupTitle, item.titleEng.toLowerCase()],
				}));
			});
		});
	}, [data.toolsListing]);

	const fuse = useMemo(
		() =>
			new Fuse(landingArray, {
				keys: [
					{ name: 'titleEng', weight: 0.9 },
					{ name: 'tags', weight: 0.1 },
				],
				includeScore: true,
				threshold: 0.25,
				minMatchCharLength: 2,
			}),
		[landingArray],
	);

	const queryLengthValid = searchQuery.trim().toLowerCase().length >= 2;

	const fuseResult = queryLengthValid
		? fuse.search(searchQuery.trim().toLowerCase())
		: [];

	const resultsToRender = fuseResult.map(r => r.item);

	const closeSearch = () => {
		AmplitudeService.logEvent(AMPLITUDE_EVENTS.ALL_TOOLS_SEARCH_CLOSED);
		setSearchQuery('');
		setSearchActive(false);
	};

	const handleSearchResultClick = (landingItem: LandingItem) => {
		let foundFeature: LandingTool | null = null;

		Object.values(data.toolsListing).forEach(category => {
			const feature = category.tools.find(toolGroup =>
				toolGroup.list.some(item => item.id === landingItem.id),
			);
			if (feature) foundFeature = feature;
		});

		if (!foundFeature) return;

		AmplitudeService.logEvent(
			AMPLITUDE_EVENTS.ALL_TOOLS_SEARCH_RESULT_PRESSED,
			{
				feature: (foundFeature as LandingTool).titleEng,
			},
		);

		setActiveTabId(landingItem.category);
		setActivePositioningId(landingItem.id);
		setActiveFeature(foundFeature as LandingTool);
		setPinnedFeatureId((foundFeature as LandingTool).id);
		setTimeout(closeSearch, 350);
	};

	useEffect(() => {
		function handleClickOutside(e: MouseEvent) {
			if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
				closeSearch();
			}
		}
		document.addEventListener('pointerdown', handleClickOutside);
		return () =>
			document.removeEventListener('pointerdown', handleClickOutside);
	}, []);

	useLayoutEffect(() => {
		function updateHeight() {
			if (!searchResultsRef.current) return;
			const rect = searchResultsRef.current.getBoundingClientRect();
			const availableHeight = window.innerHeight - rect.top - 16;
			setMaxHeight(availableHeight);
		}

		if (searchQuery.trim()) {
			updateHeight();
		}

		window.addEventListener('scroll', updateHeight, { passive: true });
		window.addEventListener('resize', updateHeight);

		return () => {
			window.removeEventListener('scroll', updateHeight);
			window.removeEventListener('resize', updateHeight);
		};
	}, [searchQuery]);

	useEffect(() => {
		if (!searchQuery.trim()) return;
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = '';
		};
	}, [searchQuery]);

	// Debounced analytics
	const debouncedLog = useDebounce(() => {
		if (!queryLengthValid) return;
		AmplitudeService.logEvent(AMPLITUDE_EVENTS.ALL_TOOLS_SEARCH_USED, {
			result: resultsToRender.length > 0 ? 'success' : 'none',
		});
	}, 500);

	useEffect(() => {
		debouncedLog();
	}, [searchQuery]);

	return (
		<motion.div
			key='search-desktop'
			ref={searchRef}
			className={styles['search']}
			variants={searchVariants}
			initial='initial'
			animate='animate'
			exit='exit'
			transition={{ duration: 0.18, ease: 'easeOut' }}
			style={{ originX: 1, width: '100%', display: 'flex' }}
		>
			<div className={styles['search']} ref={searchRef}>
				<div className={styles['search-field']}>
					<span className={styles['search-field__icon']}>🔎</span>

					<input
						id='search-input'
						type='text'
						value={searchQuery}
						onChange={e => setSearchQuery(e.target.value)}
						placeholder={tTools('heroData_searchInputPlaceholder')}
						className={styles['search-field__input']}
						autoComplete='off'
						autoCorrect='off'
						autoCapitalize='off'
						// eslint-disable-next-line jsx-a11y/no-autofocus
						autoFocus
					/>

					<button
						type='button'
						className={styles['search-field__close']}
						onClick={() => closeSearch()}
						aria-label='Close search'
					>
						<CloseIcon />
					</button>
				</div>

				<AnimatePresence>
					{queryLengthValid && (
						<motion.div
							ref={searchResultsRef}
							variants={searchResultsVariants}
							transition={{ duration: 0.15, ease: 'easeOut' }}
							className={styles['search-results']}
							style={{ height: maxHeight ?? 'auto' }}
						>
							<div className={styles['search-results__content']}>
								<div className={styles['search-results__content_scroll']}>
									{resultsToRender.length > 0 &&
										resultsToRender.map(landingItem => (
											<button
												type='button'
												key={landingItem.id}
												className={styles['search-results__item']}
												onClick={() => handleSearchResultClick(landingItem)}
											>
												<p className={styles['search-results__item_inner']}>
													{t(landingItem.title)}
												</p>
											</button>
										))}
								</div>

								<AnimatePresence mode='wait'>
									{queryLengthValid && resultsToRender.length === 0 && (
										<motion.div
											key='empty'
											variants={nothingFoundVariants}
											className={styles['search-results__empty-wrapper']}
											transition={{ duration: 0.18, ease: 'easeOut' }}
										>
											<div className={styles['search-results__empty']}>
												<p>{tTools('heroData_nothingFound1')}🤔</p>
												<p>{tTools('heroData_nothingFound2')}</p>
											</div>
										</motion.div>
									)}
								</AnimatePresence>
							</div>

							<div className={styles['search-results__footer']}>
								<button
									type='button'
									className={styles['search-results__close']}
									onClick={closeSearch}
								>
									Close <CloseIcon />
								</button>
							</div>
						</motion.div>
					)}
				</AnimatePresence>
			</div>
		</motion.div>
	);
};
